import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Trash2, EyeOff, Eye, FileText } from "lucide-react";

const CHUNK = 1024 * 1024; // 1MB chunks
const MAX = 50 * 1024 * 1024;

export default function UploadZone({ projectId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/uploads/project/${projectId}`); setFiles(data); } catch {}
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (file.size > MAX) { toast.error("File exceeds 50MB"); return; }
    setBusy(true);
    try {
      const { data: init } = await api.post("/uploads/init", {
        project_id: projectId, filename: file.name, size: file.size, content_type: file.type });
      const id = init.upload_id;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("chunk", file.slice(off, off + CHUNK));
        fd.append("offset", off);
        await api.post(`/uploads/${id}/chunk`, fd);
      }
      await api.post(`/uploads/${id}/complete`);
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); [...e.dataTransfer.files].forEach(upload); };
  const toggleExclude = async (f) => {
    try { await api.put(`/uploads/${f.id}/exclude`, { exclude: !f.exclude_from_model_context }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (f) => {
    try { await api.delete(`/uploads/${f.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="k-container" data-testid="upload-zone" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <UploadCloud size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Upload zone</h3>
      </div>
      <label onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
        data-testid="upload-dropzone"
        style={{ display: "block", border: `1px dashed ${drag ? "var(--accent)" : "var(--border)"}`,
          borderRadius: 10, padding: 20, textAlign: "center", cursor: "pointer", background: drag ? "var(--control)" : "transparent" }}>
        <input type="file" data-testid="upload-input" hidden accept=".pdf,.docx,.txt,.png,.jpg,.jpeg"
          onChange={e => [...e.target.files].forEach(upload)} />
        <span className="k-muted" style={{ fontSize: 13 }}>
          {busy ? "Uploading…" : "Drop files or click. PDF, DOCX, TXT, PNG, JPG up to 50MB."}
        </span>
      </label>
      <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>
        Files are private and treated as untrusted data. They are only sent to the model if kept included. MP3/MP4 arrive in Phase 3B.
      </p>
      {files.map(f => (
        <div key={f.id} className="k-control" data-testid={`upload-${f.id}`}
          style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span className="k-heading" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <FileText size={14} /> {f.filename}
            <span className="k-muted" style={{ fontSize: 11 }}>· {f.status}{f.exclude_from_model_context ? " · excluded" : ""}</span>
          </span>
          <span style={{ display: "flex", gap: 10 }}>
            <button className="k-link" title="Exclude from model context" data-testid={`upload-exclude-${f.id}`} onClick={() => toggleExclude(f)}>
              {f.exclude_from_model_context ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button className="k-link" title="Delete" data-testid={`upload-delete-${f.id}`} onClick={() => del(f)}><Trash2 size={15} /></button>
          </span>
        </div>
      ))}
    </div>
  );
}
